import React, { useCallback, useEffect } from 'react';
import { AssetRating } from '@goldcast/api/content';
import Icon from '@/components/atoms/Icon';
import useAssetRating from '@/hooks/useAssetRating';
import TooltipV2 from '@/components/TooltipV2';

interface HomepageAssetRatingProps {
  assetId: string;
  rating: AssetRating | null;
}

interface ThumbConfig {
  isUp: boolean;
  filledIcon: any;
  outlineIcon: string;
  tooltipMessage: string;
}

const thumbConfigs: ThumbConfig[] = [
  {
    isUp: true,
    filledIcon: 'IconThumbUpFilled',
    outlineIcon: 'IconThumbUp',
    tooltipMessage: 'Like'
  },
  {
    isUp: false,
    filledIcon: 'IconThumbDownFilled',
    outlineIcon: 'IconThumbDown',
    tooltipMessage: 'Dislike'
  }
];

const HomepageAssetRating: React.FC<HomepageAssetRatingProps> = ({ assetId, rating }) => {
  const { getAssetRating, setAssetRating, handleThumbClick } = useAssetRating();
  const { thumbsUp } = getAssetRating(assetId);

  useEffect(() => {
    setAssetRating(assetId, rating);
  }, []);

  const getIconName = useCallback(
    (config: ThumbConfig) => {
      if (config.isUp) {
        return thumbsUp ? config.filledIcon : config.outlineIcon;
      }
      return thumbsUp === false ? config.filledIcon : config.outlineIcon;
    },
    [thumbsUp]
  );

  return (
    <div className="flex flex-col gap-2 rounded-lg">
      {thumbConfigs.map(config => {
        const iconName = getIconName(config);

        return (
          <button
            key={config.isUp ? 'up' : 'down'}
            data-testid={`homepage-asset-rating-${config.isUp ? 'up' : 'down'}`}
            className="flex size-6 items-center justify-center rounded-md bg-slate-950/75 text-white transition-colors duration-200 hover:bg-slate-900"
            onClick={event => {
              event.stopPropagation();
              handleThumbClick(assetId, config.isUp);
            }}
          >
            <TooltipV2
              tooltipId={`homepage-asset-rating-${config.isUp ? 'up' : 'down'}`}
              tooltipContent={`${config.tooltipMessage}`}
              place="left"
              tooltipElement={<Icon name={iconName} className="size-4 text-white/75 hover:text-white" />}
            />
          </button>
        );
      })}
    </div>
  );
};

export default HomepageAssetRating;
